/**
 * 수정제안(correction-suggestion) react-query 훅 (M15 / Phase 1).
 *
 * - 생성: ``useCreateCorrection`` — 성공 시 Job 상세 무효화(pending 폴링 효과)
 *   + 해당 파일의 correction 이력 무효화.
 * - 이력: ``useCorrectionList`` — 편집 모달의 "수정제안 이력" 표시용.
 *
 * 채택/폐기는 기존 record diff/apply/pending 흐름(useApplyReextraction)을 사용.
 */

import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createCorrectionRequest,
  getCorrectionRequest,
  getSuggestedQuestions,
  listCorrectionRequests,
  type CorrectionEnqueueOut,
  type CorrectionRequestOut,
  type CorrectionRequestPayload,
  type CorrectionStatus,
  type SuggestedQuestionsOut,
} from "@/lib/api/corrections";
import { jobDetailKey } from "@/features/jobs/useJobDetail";

export const correctionsKeys = {
  all: ["corrections"] as const,
  list: (jobId: string, fileId: string) => ["corrections", "list", jobId, fileId] as const,
  detail: (jobId: string, fileId: string, requestId: string) =>
    ["corrections", "detail", jobId, fileId, requestId] as const,
};

/** 처리 종료(터미널) 상태 — 폴링 중단 + 행 진행 표시 해제 기준. */
const TERMINAL: ReadonlySet<CorrectionStatus> = new Set([
  "completed",
  "completed_no_change",
  "applied",
  "discarded",
  "failed",
]);

export function useCorrectionList(jobId: string, fileId: string, enabled = true) {
  return useQuery<CorrectionRequestOut[]>({
    queryKey: correctionsKeys.list(jobId, fileId),
    queryFn: () => listCorrectionRequests(jobId, fileId),
    enabled: enabled && !!jobId && !!fileId,
    staleTime: 30_000,
  });
}

interface CreateVars {
  jobId: string;
  fileId: string;
  payload: CorrectionRequestPayload;
}

export function useCreateCorrection() {
  const qc = useQueryClient();
  return useMutation<CorrectionEnqueueOut, Error, CreateVars>({
    mutationFn: ({ jobId, fileId, payload }) => createCorrectionRequest(jobId, fileId, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: jobDetailKey(vars.jobId) });
      qc.invalidateQueries({ queryKey: correctionsKeys.list(vars.jobId, vars.fileId) });
    },
  });
}

/** M21 #5 — 검수 의심사항 기반 추천 질문 생성 (kta-chat-vl). */
export function useSuggestedQuestions() {
  return useMutation<SuggestedQuestionsOut, Error, { jobId: string; fileId: string }>({
    mutationFn: ({ jobId, fileId }) => getSuggestedQuestions(jobId, fileId),
  });
}

/**
 * 진행 중인 correction 의 상태를 폴링(2.5s)하다 터미널 상태에 도달하면
 * Job 상세를 무효화(pending_extraction 반영)하고 ``onTerminal`` 을 호출한다.
 * 행의 "처리 중" 링을 해제하는 데 사용.
 */
export function useCorrectionStatusWatcher(
  jobId: string,
  fileId: string,
  requestId: string,
  onTerminal: (status: CorrectionStatus) => void,
) {
  const qc = useQueryClient();
  const query = useQuery<CorrectionRequestOut>({
    queryKey: correctionsKeys.detail(jobId, fileId, requestId),
    queryFn: () => getCorrectionRequest(jobId, fileId, requestId),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && TERMINAL.has(s) ? false : 2500;
    },
  });

  const status = query.data?.status;
  useEffect(() => {
    if (status && TERMINAL.has(status)) {
      qc.invalidateQueries({ queryKey: jobDetailKey(jobId) });
      // 이력(list)도 갱신 → 무변경/실패 결과와 llm_response(사유)가 최신으로
      // 반영되어, 케이스 이동 후 돌아와도 결과를 지속 표시할 수 있다.
      qc.invalidateQueries({ queryKey: correctionsKeys.list(jobId, fileId) });
      onTerminal(status);
    }
  }, [status, jobId, fileId, qc, onTerminal]);

  return query.data?.status ?? null;
}
